/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  Switch,
  Input,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { checkToken } from 'api/utils';
import { getUsuarios } from 'api/usuarios/usuarios';
import {
  getConfiguradoresNombramientoUsuarioNotificar,
  postConfiguradorNombramientoUsuarioNotificar,
  deleteConfiguradorNombramientoUsuarioNotificar,
  patchConfiguradorNombramientoUsuarioNotificar,
  getConfiguradorNombramientoUsuarioNotificar,
} from 'api/catalogos/ConfiguradorNombramientoUsuarioNotificar';

export const configuradorNombramientoUsuarioNotificarPermission = {
  permissionModel: 'configuradornombramientousuarionotificar',
  catalogsModels: [],
  tabsModels: [],
};

const ConfiguradorNombramientoUsuarioNotificar = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [selectedData, setSelectedData] = useState({});

  const formInstances = [form];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getConfiguradoresNombramientoUsuarioNotificar();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [_data, _usuarios] = await Promise.all([
        getConfiguradoresNombramientoUsuarioNotificar(),
        getUsuarios(),
      ]);
      if (mounted) {
        setData(formatReceived(_data));
        setUsuarios(Array.isArray(_usuarios) ? _usuarios : (_usuarios?.results ?? []));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _registro = await getConfiguradorNombramientoUsuarioNotificar(match.id);
    setTimeout(() => {
      if (_registro) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_registro));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchConfiguradorNombramientoUsuarioNotificar(key, values)
        : postConfiguradorNombramientoUsuarioNotificar(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteConfiguradorNombramientoUsuarioNotificar(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    form.setFieldsValue({ alerta: true });
    setVisible(true);
  };

  const isEditing = selectedRowKeys.length > 0;

  const columns = [
    {
      titleText: 'Correo Electrónico',
      dataIndex: 'descripcion_usuario_email',
      key: 'descripcion_usuario_email',
      width: 280,
    },
    {
      titleText: 'Nombre',
      dataIndex: 'descripcion_usuario_nombre',
      key: 'descripcion_usuario_nombre',
      width: 180,
    },
    {
      titleText: 'Apellido Paterno',
      dataIndex: 'descripcion_usuario_apellido_paterno',
      key: 'descripcion_usuario_apellido_paterno',
      width: 180,
    },
    {
      titleText: 'Apellido Materno',
      dataIndex: 'descripcion_usuario_apellido_materno',
      key: 'descripcion_usuario_apellido_materno',
      width: 180,
    },
    {
      titleText: 'Alerta',
      dataIndex: 'alerta',
      key: 'alerta',
      width: 100,
      render: (val) => (val ? 'Sí' : 'No'),
    },
  ];

  const requiredRule = { required: true, message: 'El campo es requerido' };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete: () => setVisibleAlert(true),
          }}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          width={520}
          title={(
            <FormSubmitControls
              label={`${isEditing ? 'Editar' : 'Agregar'} Usuario a Notificar`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="configurador_nombramiento_usuario_notificar"
            initialValues={{ alerta: true }}
          >
            <Col xs={24}>
              <Form.Item
                name="usuario"
                rules={[requiredRule]}
                label="Usuario"
                hasFeedback
              >
                <Select
                  dataSource={usuarios}
                  placeholder="Selecciona el usuario"
                  render={(u) => {
                    const nombre = [u.first_name, u.last_name, u.second_last_name]
                      .filter(Boolean).join(' ');
                    return nombre ? `${nombre} (${u.email})` : u.email;
                  }}
                />
              </Form.Item>
            </Col>

            {isEditing && (
              <>
                <Col xs={24}>
                  <Form.Item
                    name="descripcion_usuario_email"
                    label="Correo Electrónico"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    name="descripcion_usuario_nombre"
                    label="Nombre"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    name="descripcion_usuario_apellido_paterno"
                    label="Apellido Paterno"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    name="descripcion_usuario_apellido_materno"
                    label="Apellido Materno"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
              </>
            )}

            <Col xs={24}>
              <Form.Item
                name="alerta"
                label="Alerta activa"
                valuePropName="checked"
              >
                <Switch checkedChildren="Sí" unCheckedChildren="No" />
              </Form.Item>
            </Col>

            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="Usuario seleccionado"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default ConfiguradorNombramientoUsuarioNotificar;
